/**
 * Competition configuration
 *
 * Centralizes competition settings for the frontend.
 * All supported football leagues with their API-Football IDs,
 * matchday counts, season formats, and season split configurations.
 *
 * The league's league_configs row (driven by settings.competition_id)
 * is the runtime source of truth. These definitions serve as templates
 * for league creation and as fallback when league_configs is missing.
 */

export interface SeasonSplit {
  name: string;
  startMatchday: number;
  endMatchday: number;
}

export interface CompetitionConfig {
  /** API-Football league ID */
  id: number;
  /** Display name */
  name: string;
  /** Short code */
  code: string;
  /** Country or region */
  country: string;
  /** Country flag emoji */
  flag: string;
  /** Competition logo (local asset) */
  logo: string;
  /** Number of matchdays per season */
  matchdays: number;
  /** Number of teams in the competition */
  teamCount: number;
  /** Apertura end matchday (for split standings) — deprecated, use seasonSplits */
  aperturaEnd: number;
  /** Season format: 'YYYY-YY' for cross-year, 'YYYY' for calendar-year */
  seasonFormat: 'YYYY-YY' | 'YYYY' | 'YYYY-A/C';
  /** Season splits (null = auto-compute 2 halves from matchdays) */
  seasonSplits: SeasonSplit[] | null;
  /** Default TDD-ineligible count (top N from previous season) */
  tddIneligibleCount: number;
  /** Whether this competition has promotion/relegation (default: false) */
  hasPromotion?: boolean;
}

// ---------------------------------------------------------------------------
// Logo imports
// ---------------------------------------------------------------------------

import logo39 from '@/assets/logos/39.png';
import logo140 from '@/assets/logos/140.png';
import logo78 from '@/assets/logos/78.png';
import logo135 from '@/assets/logos/135.png';
import logo61 from '@/assets/logos/61.png';
import logo253 from '@/assets/logos/253.png';
import logo262 from '@/assets/logos/262.png';
import logo94 from '@/assets/logos/94.png';
import logo88 from '@/assets/logos/88.png';
import logo307 from '@/assets/logos/307.png';

// ---------------------------------------------------------------------------
// Competition definitions — single data array
// ---------------------------------------------------------------------------

const COMPETITION_DATA: CompetitionConfig[] = [
  {
    id: 39,
    name: 'Premier League',
    code: 'EPL',
    country: 'England',
    flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
    logo: logo39,
    matchdays: 38,
    teamCount: 20,
    aperturaEnd: 19,
    seasonFormat: 'YYYY-YY',
    seasonSplits: [
      { name: 'Apertura', startMatchday: 1, endMatchday: 19 },
      { name: 'Clausura', startMatchday: 20, endMatchday: 38 },
    ],
    tddIneligibleCount: 6,
    hasPromotion: true,
  },
  {
    id: 140,
    name: 'La Liga',
    code: 'LIGA',
    country: 'Spain',
    flag: '🇪🇸',
    logo: logo140,
    matchdays: 38,
    teamCount: 20,
    aperturaEnd: 19,
    seasonFormat: 'YYYY-YY',
    seasonSplits: [
      { name: 'Apertura', startMatchday: 1, endMatchday: 19 },
      { name: 'Clausura', startMatchday: 20, endMatchday: 38 },
    ],
    tddIneligibleCount: 6,
    hasPromotion: true,
  },
  {
    id: 78,
    name: 'Bundesliga',
    code: 'BL',
    country: 'Germany',
    flag: '🇩🇪',
    logo: logo78,
    matchdays: 34,
    teamCount: 18,
    aperturaEnd: 17,
    seasonFormat: 'YYYY-YY',
    seasonSplits: [
      { name: 'Hinrunde', startMatchday: 1, endMatchday: 17 },
      { name: 'Rückrunde', startMatchday: 18, endMatchday: 34 },
    ],
    tddIneligibleCount: 6,
    hasPromotion: true,
  },
  {
    id: 135,
    name: 'Serie A',
    code: 'SA',
    country: 'Italy',
    flag: '🇮🇹',
    logo: logo135,
    matchdays: 38,
    teamCount: 20,
    aperturaEnd: 19,
    seasonFormat: 'YYYY-YY',
    seasonSplits: [
      { name: 'Andata', startMatchday: 1, endMatchday: 19 },
      { name: 'Ritorno', startMatchday: 20, endMatchday: 38 },
    ],
    tddIneligibleCount: 6,
    hasPromotion: true,
  },
  {
    id: 61,
    name: 'Ligue 1',
    code: 'L1',
    country: 'France',
    flag: '🇫🇷',
    logo: logo61,
    matchdays: 34,
    teamCount: 18,
    aperturaEnd: 17,
    seasonFormat: 'YYYY-YY',
    seasonSplits: [
      { name: 'Aller', startMatchday: 1, endMatchday: 17 },
      { name: 'Retour', startMatchday: 18, endMatchday: 34 },
    ],
    tddIneligibleCount: 6,
    hasPromotion: true,
  },
  {
    id: 253,
    name: 'Major League Soccer',
    code: 'MLS',
    country: 'USA',
    flag: '🇺🇸',
    logo: logo253,
    matchdays: 34,
    teamCount: 30,
    aperturaEnd: 17,
    seasonFormat: 'YYYY',
    seasonSplits: [
      { name: '1H', startMatchday: 1, endMatchday: 17 },
      { name: '2H', startMatchday: 18, endMatchday: 34 },
    ],
    tddIneligibleCount: 6,
    hasPromotion: false,
  },
  {
    id: 262,
    name: 'Liga MX',
    code: 'LMX',
    country: 'Mexico',
    flag: '🇲🇽',
    logo: logo262,
    matchdays: 17,
    teamCount: 18,
    aperturaEnd: 9,
    seasonFormat: 'YYYY-A/C',
    seasonSplits: null,
    tddIneligibleCount: 6,
    hasPromotion: false,
  },
  {
    id: 94,
    name: 'Primeira Liga',
    code: 'PL',
    country: 'Portugal',
    flag: '🇵🇹',
    logo: logo94,
    matchdays: 34,
    teamCount: 18,
    aperturaEnd: 17,
    seasonFormat: 'YYYY-YY',
    seasonSplits: [
      { name: 'First Half', startMatchday: 1, endMatchday: 17 },
      { name: 'Second Half', startMatchday: 18, endMatchday: 34 },
    ],
    tddIneligibleCount: 6,
    hasPromotion: true,
  },
  {
    id: 88,
    name: 'Eredivisie',
    code: 'ERE',
    country: 'Netherlands',
    flag: '🇳🇱',
    logo: logo88,
    matchdays: 34,
    teamCount: 18,
    aperturaEnd: 17,
    seasonFormat: 'YYYY-YY',
    seasonSplits: [
      { name: 'First Half', startMatchday: 1, endMatchday: 17 },
      { name: 'Second Half', startMatchday: 18, endMatchday: 34 },
    ],
    tddIneligibleCount: 6,
    hasPromotion: true,
  },
  {
    id: 307,
    name: 'Saudi Pro League',
    code: 'SPL',
    country: 'Saudi Arabia',
    flag: '🇸🇦',
    logo: logo307,
    matchdays: 30,
    teamCount: 18,
    aperturaEnd: 15,
    seasonFormat: 'YYYY-YY',
    seasonSplits: [
      { name: 'First Half', startMatchday: 1, endMatchday: 15 },
      { name: 'Second Half', startMatchday: 16, endMatchday: 30 },
    ],
    tddIneligibleCount: 6,
    hasPromotion: true,
  },
];

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

/** All competitions as a flat array (useful for dropdowns / iteration) */
export const COMPETITIONS_LIST: CompetitionConfig[] = COMPETITION_DATA;

/** All supported competitions keyed by code */
export const COMPETITIONS: Record<string, CompetitionConfig> = Object.fromEntries(
  COMPETITION_DATA.map(c => [c.code, c]),
);

/** Default competition used across the app */
export const DEFAULT_COMPETITION: CompetitionConfig = COMPETITIONS['EPL'];

/**
 * Compute default 2-half season splits from total matchdays.
 * Used as fallback when a competition has no explicit seasonSplits.
 */
export function computeDefaultSplits(totalMatchdays: number): SeasonSplit[] {
  const midpoint = Math.ceil(totalMatchdays / 2);
  return [
    { name: '1H', startMatchday: 1, endMatchday: midpoint },
    { name: '2H', startMatchday: midpoint + 1, endMatchday: totalMatchdays },
  ];
}

/**
 * Get competition config by API-Football league ID.
 * Falls back to DEFAULT_COMPETITION if not found.
 */
export function getCompetitionById(competitionId: number): CompetitionConfig {
  return COMPETITIONS_LIST.find(c => c.id === competitionId) || DEFAULT_COMPETITION;
}

/**
 * Get competition config from league settings.
 * Falls back to DEFAULT_COMPETITION if not configured.
 */
export function getCompetitionFromSettings(
  settings?: Record<string, unknown> | null
): CompetitionConfig {
  if (settings && typeof settings.competition_id === 'number') {
    return getCompetitionById(settings.competition_id);
  }
  return DEFAULT_COMPETITION;
}
